/**
 * Staging left-nav: duplicate-email cluster + profile-completeness fields.
 * Completeness field counts come from /api/students/completeness/stats so they
 * match the Student profile completeness page.
 */

import {
  CATEGORY_LABELS,
  PROFILE_COMPLETENESS_FIELDS,
} from '../domain/studentCompleteness/profileCompletenessFields';

export const STAGING_DUPLICATE_CATEGORY = {
  id: 'duplicate_or_match',
  label: 'Duplicate email in upload',
  description: 'Same email on 2+ Excel/CSV rows — accept one, merge fields, or add enrollment',
};

/** Single-row match against SQL (not multi-row Excel duplicates). */
export const STAGING_EXISTING_MATCH_CATEGORY = {
  id: 'existing_match',
  label: 'Already in database',
  description: 'One upload row matches an existing student — review update / enrollment',
};

/** Named upload rows with no real email — kept in staging until an address is added. */
export const STAGING_NAMES_ONLY_CATEGORY = {
  id: 'missing_email',
  label: 'Names only (no email)',
  description: 'Row has a name but no email — kept here so it is not lost. Add a real address to save.',
};

/** @deprecated kept for callers that still iterate the old bundled list */
export const STAGING_REVIEW_CATEGORIES = [
  STAGING_DUPLICATE_CATEGORY,
  STAGING_EXISTING_MATCH_CATEGORY,
  STAGING_NAMES_ONLY_CATEGORY,
  {
    id: 'missing_first_name',
    label: 'Missing first name',
    description: 'First name is blank',
  },
  {
    id: 'missing_last_name',
    label: 'Missing last name',
    description: 'Last name is blank or was filled as a default',
  },
  {
    id: 'missing_phone',
    label: 'Missing phone',
    description: 'Phone / mobile is blank',
  },
  {
    id: 'ambiguous_first_name',
    label: 'Ambiguous first name',
    description: 'Placeholder first name (e.g. Unknown)',
  },
  {
    id: 'ambiguous_last_name',
    label: 'Ambiguous last name',
    description: 'Placeholder last name (e.g. Unknown)',
  },
  {
    id: 'enrollment',
    label: 'Enrollment & batch',
    description: 'Ambiguous batch, missing batch, new enrollment, or fee without batch',
  },
  {
    id: 'field_discrepancy',
    label: 'Field discrepancies vs database',
    description: 'Import values differ from the matched SQL student',
  },
  {
    id: 'conflicts',
    label: 'Conflicts & validation',
    description: 'Hard conflicts, invalid values, or failed apply',
  },
  {
    id: 'unmapped_fields',
    label: 'Unmapped student fields',
    description: 'Extra columns kept in payload only',
  },
  {
    id: 'other',
    label: 'Other student review',
    description: 'Other rows needing admin action',
  },
];

const REASON_TO_CATEGORY = {
  missing_email: 'missing_email',
  incomplete_first_name: 'missing_first_name',
  incomplete_last_name: 'missing_last_name',
  incomplete_phone: 'missing_phone',
  ambiguous_first_name: 'ambiguous_first_name',
  ambiguous_last_name: 'ambiguous_last_name',
  ambiguous_or_missing_batch: 'enrollment',
  missing_batch: 'enrollment',
  ambiguous_batch: 'enrollment',
  fee_without_enrollment: 'enrollment',
  new_enrollment_for_existing_student: 'existing_match',
  duplicate_email_in_file: 'duplicate_or_match',
  existing_student_update: 'existing_match',
  existing_student_field_discrepancy: 'field_discrepancy',
  // Guidance flag for merge/enroll UI — not proof of multi-row Excel duplicate.
  multi_row_merge_or_enroll: 'existing_match',
  has_unmapped_fields: 'unmapped_fields',
  confidence_medium: 'other',
  confidence_low: 'other',
};

const CATEGORY_PRIORITY = STAGING_REVIEW_CATEGORIES.map((c) => c.id);

function isDuplicateEmailConflictText(text) {
  return /duplicate email/i.test(String(text || ''));
}

function emailSortKey(doc) {
  return String(doc?.emailKey || doc?.ruleResult?.email || '')
    .trim()
    .toLowerCase();
}

/**
 * If the review queue itself contains the same email more than once, treat those
 * rows as in-file duplicates even when ruleResult.emailOccurrenceCount was 1.
 */
export function annotateQueueEmailDuplicates(items) {
  const list = Array.isArray(items) ? items : [];
  const counts = new Map();
  for (const doc of list) {
    const key = emailSortKey(doc);
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }

  return list.map((doc) => {
    const key = emailSortKey(doc);
    const queueCount = key ? counts.get(key) || 1 : 1;
    if (queueCount <= 1) return doc;

    const prev = doc.ruleResult || {};
    const priorCount = Math.max(1, Number(prev.emailOccurrenceCount) || 1);
    const emailOccurrenceCount = Math.max(priorCount, queueCount);
    const reviewReasons = [...(prev.reviewReasons || [])];
    if (!reviewReasons.includes('duplicate_email_in_file')) {
      reviewReasons.push('duplicate_email_in_file');
    }
    if (!reviewReasons.includes('multi_row_merge_or_enroll')) {
      reviewReasons.push('multi_row_merge_or_enroll');
    }

    return {
      ...doc,
      ruleResult: {
        ...prev,
        emailOccurrenceCount,
        reviewReasons,
      },
    };
  });
}

/**
 * Count how many queue docs share each email (case-insensitive).
 * @param {object[]} items
 * @returns {Map<string, number>}
 */
export function buildQueueEmailPeerCounts(items) {
  const counts = new Map();
  for (const doc of items || []) {
    const key = emailSortKey(doc);
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

/**
 * Live multi-row duplicate in the *current* review queue (not stale occurrence flags).
 * @param {object} doc
 * @param {Map<string, number>|object[]} peerCountsOrItems
 */
export function isLiveInFileDuplicateDoc(doc, peerCountsOrItems) {
  const counts =
    peerCountsOrItems instanceof Map
      ? peerCountsOrItems
      : buildQueueEmailPeerCounts(peerCountsOrItems);
  const key = emailSortKey(doc);
  return Boolean(key && (counts.get(key) || 0) > 1);
}

/**
 * Stored rule flags that *claimed* an in-file duplicate (may be stale if siblings left the queue).
 */
export function hasStoredInFileDuplicateFlag(doc) {
  const rr = doc?.ruleResult || {};
  if (Number(rr.emailOccurrenceCount) > 1) return true;
  const reasons = rr.reviewReasons || [];
  if (reasons.includes('duplicate_email_in_file')) return true;
  const conflicts = rr.conflicts || [];
  if (
    conflicts.some((c) => /duplicate email in this upload\s*\(\s*[2-9]|\d{2,}/i.test(String(c)))
  ) {
    return true;
  }
  return false;
}

/**
 * True multi-row Excel/CSV duplicates.
 * Live queue peers (>= 2) or stored ingest flags (siblings may be off this page).
 */
export function isInFileDuplicateDoc(doc, peerCountsOrItems) {
  if (peerCountsOrItems != null && isLiveInFileDuplicateDoc(doc, peerCountsOrItems)) {
    return true;
  }
  return hasStoredInFileDuplicateFlag(doc);
}

/** Named row with no real email (placeholder pending.*@no-email.sms.local). */
export function isNamesOnlyStagingDoc(doc, peerCountsOrItems) {
  if (isInFileDuplicateDoc(doc, peerCountsOrItems)) return false;
  const rr = doc?.ruleResult || {};
  if (rr.pendingEmail) return true;
  if ((rr.reviewReasons || []).includes('missing_email')) return true;
  const email = String(doc?.emailKey || rr.email || '').trim().toLowerCase();
  return email.endsWith('@no-email.sms.local');
}

/**
 * Single upload row that matches a student/user already in SQL.
 * Excludes live in-file duplicates still sitting together in the queue.
 */
export function isExistingMatchDoc(doc, peerCountsOrItems) {
  const liveKnown = peerCountsOrItems != null;
  if (liveKnown && isLiveInFileDuplicateDoc(doc, peerCountsOrItems)) {
    return false;
  }
  if (!liveKnown && hasStoredInFileDuplicateFlag(doc)) {
    return false;
  }
  if (isNamesOnlyStagingDoc(doc, peerCountsOrItems)) {
    return false;
  }
  const rr = doc?.ruleResult || {};
  const reasons = rr.reviewReasons || [];
  if (reasons.includes('existing_student_update')) return true;
  if (reasons.includes('new_enrollment_for_existing_student')) return true;
  if (rr.matchStatus === 'existing') return true;
  if (String(doc?.status || '') === 'applied_partial') {
    // Placeholder / missing-email rows are incompleteness work, not SQL matches.
    if (!(rr.pendingEmail || reasons.includes('missing_email'))) return true;
  }
  const conflictText = (rr.conflicts || []).join(' ').toLowerCase();
  if (/already exists|already belongs/i.test(conflictText)) return true;
  return false;
}

function nonDuplicateConflicts(doc) {
  return (doc?.ruleResult?.conflicts || []).filter((c) => !isDuplicateEmailConflictText(c));
}

function hasHardConflict(doc) {
  if (doc?.status === 'failed') return true;
  return nonDuplicateConflicts(doc).length > 0;
}

function isQueueDuplicateDoc(doc, peerCountsOrItems) {
  if (peerCountsOrItems != null) {
    return isLiveInFileDuplicateDoc(doc, peerCountsOrItems);
  }
  return hasStoredInFileDuplicateFlag(doc);
}

export function classifyStagingReviewDoc(doc, peerCountsOrItems) {
  const matched = new Set();
  const reasons = doc?.ruleResult?.reviewReasons || [];
  const liveDup = isQueueDuplicateDoc(doc, peerCountsOrItems);

  if (liveDup) matched.add('duplicate_or_match');
  else if (isNamesOnlyStagingDoc(doc, peerCountsOrItems)) matched.add('missing_email');
  else if (isExistingMatchDoc(doc, peerCountsOrItems)) matched.add('existing_match');
  if (hasHardConflict(doc)) matched.add('conflicts');

  for (const reason of reasons) {
    const cat = REASON_TO_CATEGORY[reason];
    if (cat === 'duplicate_or_match' && !liveDup) continue;
    if (cat) matched.add(cat);
  }

  if (!matched.size) matched.add('other');

  return CATEGORY_PRIORITY.filter((id) => matched.has(id));
}

export function primaryStagingReviewCategory(doc, peerCountsOrItems) {
  if (isQueueDuplicateDoc(doc, peerCountsOrItems)) return 'duplicate_or_match';
  if (isNamesOnlyStagingDoc(doc, peerCountsOrItems)) return 'missing_email';
  if (isExistingMatchDoc(doc, peerCountsOrItems)) return 'existing_match';
  const cats = classifyStagingReviewDoc(doc, peerCountsOrItems);
  return cats[0] || 'other';
}

function sortDuplicatePeers(peers) {
  return [...peers].sort((a, b) => {
    const byEmail = emailSortKey(a).localeCompare(emailSortKey(b));
    if (byEmail !== 0) return byEmail;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

export function groupStagingReviewItems(items) {
  const buckets = Object.fromEntries(STAGING_REVIEW_CATEGORIES.map((c) => [c.id, []]));
  const annotated = annotateQueueEmailDuplicates(items);
  const peerCounts = buildQueueEmailPeerCounts(annotated);

  for (const doc of annotated) {
    const primary = primaryStagingReviewCategory(doc, peerCounts);
    if (!buckets[primary]) buckets.other.push(doc);
    else buckets[primary].push(doc);
  }

  if (buckets.duplicate_or_match?.length) {
    buckets.duplicate_or_match = sortDuplicatePeers(buckets.duplicate_or_match);
  }
  if (buckets.existing_match?.length) {
    buckets.existing_match = sortDuplicatePeers(buckets.existing_match);
  }

  return STAGING_REVIEW_CATEGORIES.map((meta) => ({
    ...meta,
    items: buckets[meta.id] || [],
  })).filter((group) => group.items.length > 0);
}

export function getNextInCategory(items, currentId) {
  if (!currentId || !items?.length) return null;
  const current = items.find((d) => d.id === currentId);
  if (!current) return null;
  const peerCounts = buildQueueEmailPeerCounts(items);
  const cat = primaryStagingReviewCategory(current, peerCounts);
  let peers = items.filter((d) => primaryStagingReviewCategory(d, peerCounts) === cat);
  if (cat === 'duplicate_or_match' || cat === 'existing_match') {
    peers = sortDuplicatePeers(peers);
  }
  const idx = peers.findIndex((d) => d.id === currentId);
  if (idx < 0 || idx >= peers.length - 1) return null;
  return peers[idx + 1];
}

export function getCategoryPosition(items, currentId) {
  if (!currentId || !items?.length) return null;
  const current = items.find((d) => d.id === currentId);
  if (!current) return null;
  const peerCounts = buildQueueEmailPeerCounts(items);
  const categoryId = primaryStagingReviewCategory(current, peerCounts);
  const meta = STAGING_REVIEW_CATEGORIES.find((c) => c.id === categoryId);
  let peers = items.filter((d) => primaryStagingReviewCategory(d, peerCounts) === categoryId);
  if (categoryId === 'duplicate_or_match' || categoryId === 'existing_match') {
    peers = sortDuplicatePeers(peers);
  }
  const idx = peers.findIndex((d) => d.id === currentId);
  return {
    index: idx + 1,
    total: peers.length,
    categoryId,
    categoryLabel: meta?.label || categoryId,
  };
}

export function reasonLabel(code) {
  const map = {
    missing_email: 'Missing email',
    incomplete_first_name: 'Incomplete first name',
    incomplete_last_name: 'Incomplete last name',
    ambiguous_first_name: 'Ambiguous first name',
    ambiguous_last_name: 'Ambiguous last name (e.g. Unknown)',
    incomplete_phone: 'Missing phone number',
    ambiguous_or_missing_batch: 'Ambiguous or missing batch',
    missing_batch: 'Course without a real batch',
    ambiguous_batch: 'Ambiguous batch',
    fee_without_enrollment: 'Fee without enrollment',
    new_enrollment_for_existing_student: 'New enrollment for existing student',
    duplicate_email_in_file: 'Duplicate email in this upload',
    existing_student_update: 'Existing student — flagged to update',
    existing_student_field_discrepancy: 'Import differs from database fields',
    multi_row_merge_or_enroll: 'Choose accept, merge, or add enrollment',
    has_unmapped_fields: 'Has unmapped fields',
    confidence_medium: 'Medium confidence',
    confidence_low: 'Low confidence',
  };
  return map[code] || code;
}

/** Completeness field nav; missingCount matches Student profile completeness. */
export function completenessFieldGroupsFromStats(stats) {
  const byField = Array.isArray(stats?.byField) ? stats.byField : [];
  const countByKey = Object.fromEntries(byField.map((f) => [f.key, Number(f.missingCount) || 0]));
  const categoryCount = Object.fromEntries(
    (stats?.byCategory || []).map((c) => [c.category, Number(c.missingStudentCount) || 0])
  );

  const fields = (byField.length ? byField : PROFILE_COMPLETENESS_FIELDS).map((f) => ({
    key: f.key,
    label: f.label,
    category: f.category,
    missingCount: countByKey[f.key] != null ? Number(countByKey[f.key]) : Number(f.missingCount) || 0,
  }));

  return Object.keys(CATEGORY_LABELS).map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    missingStudentCount:
      categoryCount[category] != null ? Number(categoryCount[category]) : 0,
    fields: fields.filter((f) => f.category === category),
  })).filter((group) => group.fields.length > 0);
}
